import type { TargetingProviderConfig } from '@openx/types/targeting/targetingProviderConfig';
import { TechnologyAndDevicesOption } from '@openx/types/targeting/technologyAndDevices';
import { useMemo } from 'react';
import { useApiFrameworksOptionsFetch } from '../../targeting/utils/useApiFrameworksOptionsFetch';
import { useBrowserOptionsFetch } from '../../targeting/utils/useBrowserOptionsFetch';
import { useOldDeviceTypesOptionsFetch } from '../../targeting/utils/useOldDeviceTypesOptionsFetch';
import { useOsOptionsFetch } from '../../targeting/utils/useOsOptionsFetch';
import { useTargetingLanguagesOptionsFetch } from '../../targeting/utils/useTargetingLanguagesOptionsFetch';

export const useTechnologyOptionsFetch: TargetingProviderConfig['useTechnologyOptionsFetch'] = (
  type: TechnologyAndDevicesOption,
  isUsed = true
) => {
  // isUsed + type check needed to prevent fetch of options when option is not used
  const deviceTypes = useOldDeviceTypesOptionsFetch({
    enabled: isUsed && type === TechnologyAndDevicesOption.DEVICE_TYPE,
  });
  const os = useOsOptionsFetch({ enabled: isUsed && type === TechnologyAndDevicesOption.OS });
  const apiFrameworks = useApiFrameworksOptionsFetch({
    enabled: isUsed && type === TechnologyAndDevicesOption.API_FRAMEWORK,
  });
  const browsers = useBrowserOptionsFetch({ enabled: isUsed && type === TechnologyAndDevicesOption.BROWSER });
  const languages = useTargetingLanguagesOptionsFetch({
    enabled: isUsed && type === TechnologyAndDevicesOption.LANGUAGE,
  });

  return useMemo(
    () =>
      ({
        [TechnologyAndDevicesOption.DEVICE_TYPE]: {
          isLoading: deviceTypes.loading,
          options: deviceTypes.mapById,
        },
        [TechnologyAndDevicesOption.OS]: {
          isLoading: os.loading,
          options: os.mapById,
        },
        [TechnologyAndDevicesOption.BROWSER]: {
          isLoading: browsers.loading,
          options: browsers.mapById,
        },
        [TechnologyAndDevicesOption.LANGUAGE]: {
          isLoading: languages.loading,
          options: languages.mapById,
        },
        [TechnologyAndDevicesOption.API_FRAMEWORK]: {
          isLoading: apiFrameworks.loading,
          options: apiFrameworks.mapById,
        },
      })[type],
    [
      apiFrameworks.mapById,
      apiFrameworks.loading,
      browsers.mapById,
      browsers.loading,
      deviceTypes.mapById,
      deviceTypes.loading,
      languages.mapById,
      languages.loading,
      os.mapById,
      os.loading,
      type,
    ]
  );
};
